import api from '../../auth/apiClient';
import { SAKTI_API_CONFIG } from '../apiConfig';
import {
  SaktiDataViewerQuery,
  SaktiSyncEntitiesResponse,
  SaktiSyncJob,
  SaktiTableRowsResponse,
  SaktiTablesResponse,
} from '../types';

/**
 * Client for the Configuration feature. Every call hits the FastAPI insight-api
 * gateway (`/api/pijar/config/*`), which proxies to Sakti Go's reporting service.
 *
 * The five endpoints do NOT share one envelope — the sync endpoints return the
 * worker's bare Job/entity-list shapes, the data-viewer endpoints return their own
 * `{ success, ... }` shapes — so each method returns its own typed response.
 */
export class SaktiConfigApiService {
  // GET /sync/entities — list the master-data entities that can be synced.
  static async listSyncEntities(): Promise<SaktiSyncEntitiesResponse> {
    const response = await api.get<SaktiSyncEntitiesResponse>(
      SAKTI_API_CONFIG.CONFIG_SYNC_ENTITIES_PATH
    );

    return response.data;
  }

  // POST /sync/jobs — trigger an async sync. `["all"]` (or empty) means a full sync.
  // Returns the bare Job immediately (HTTP 202); poll getSyncJob for progress.
  static async startSyncJob(entities: string[]): Promise<SaktiSyncJob> {
    const response = await api.post<SaktiSyncJob>(SAKTI_API_CONFIG.CONFIG_SYNC_JOBS_PATH, {
      entities,
    });

    return response.data;
  }

  // GET /sync/jobs/{id} — poll the live status + per-entity progress of a job.
  static async getSyncJob(jobId: string): Promise<SaktiSyncJob> {
    const response = await api.get<SaktiSyncJob>(
      SAKTI_API_CONFIG.CONFIG_SYNC_JOB_DETAIL_PATH(jobId)
    );

    return response.data;
  }

  // GET /tables — list browsable master/trx tables (sensitive tables excluded upstream).
  static async listTables(): Promise<SaktiTablesResponse> {
    const response = await api.get<SaktiTablesResponse>(SAKTI_API_CONFIG.CONFIG_TABLES_PATH);

    return response.data;
  }

  // GET /tables/{table}/rows — one page of rows; omits absent query params.
  static async getTableRows(query: SaktiDataViewerQuery): Promise<SaktiTableRowsResponse> {
    const params: Record<string, string | number> = {
      page: query.page,
      limit: query.limit,
    };

    if (query.search) {
      params.search = query.search;
    }

    if (query.sortBy) {
      params.sort_by = query.sortBy;
    }

    if (query.sortDir) {
      params.sort_dir = query.sortDir;
    }

    const response = await api.get<SaktiTableRowsResponse>(
      SAKTI_API_CONFIG.CONFIG_TABLE_ROWS_PATH(query.table),
      { params }
    );

    return response.data;
  }
}

export default SaktiConfigApiService;
